import { type ComponentType, type ReactNode } from 'react';
import { Inbox, type LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * EmptyState — single primitive for "nothing to show yet" surfaces.
 * Replaces ad-hoc `rounded-lg border border-dashed p-10 text-center…`
 * markup peppered through the app.
 *
 *   <EmptyState
 *     icon={FolderKanban}
 *     title="No projects yet"
 *     description="Create one to start auditing."
 *     action={<Button>New project</Button>}
 *   />
 */
export interface EmptyStateProps {
  icon?: ComponentType<LucideProps>;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  variant?: 'card' | 'inline';
  className?: string;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  variant = 'card',
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        variant === 'card'
          ? 'rounded-xl border border-dashed border-border/70 bg-card/40 p-10 sm:p-14'
          : 'p-6',
        className,
      )}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60">
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden />
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {description ? (
          <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
